import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type {
  ContractTermsResponse,
  OrgResponse,
} from "@bw/shared/types/generated";

import { StatusBadge } from "@/components/StatusBadge";
import { orgBusinessName, orgLegalName } from "@/shared/orgDisplay";

import { ApprovalBlockersPanel } from "./ApprovalBlockersPanel";
import { CommercialAgreement } from "./CommercialAgreement";
import { FinalDecision } from "./FinalDecision";
import { LaunchReadiness } from "./LaunchReadiness";
import { ownerDisplayName } from "./terms";

export type ApprovalReadState = "loading" | "error" | "ready";

function ReadError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Alert
      severity="error"
      role="alert"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      {message}
    </Alert>
  );
}

export function ApprovalDetails({
  orgId,
  org,
  terms,
  orgState,
  termsState,
  actionError,
  actionPending,
  approvePending,
  startReviewPending,
  onRetryOrg,
  onRetryTerms,
  onClose,
  onStartReview,
  onApprove,
  onReject,
}: {
  orgId: string;
  org: OrgResponse | undefined;
  terms: ContractTermsResponse | null | undefined;
  orgState: ApprovalReadState;
  termsState: ApprovalReadState;
  actionError: string | null;
  actionPending: boolean;
  approvePending: boolean;
  startReviewPending: boolean;
  onRetryOrg: () => void;
  onRetryTerms: () => void;
  onClose: () => void;
  onStartReview: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const hasReadError = orgState === "error" || termsState === "error";
  const isReadLoading = orgState === "loading" || termsState === "loading";
  const readyOrg = org?.org_id === orgId ? org : undefined;

  return (
    <Box
      component="section"
      id={`approval-details-${orgId}`}
      aria-label="Selected approval details"
      sx={{ bgcolor: "action.hover", p: { xs: 2, md: 2.5 } }}
    >
      {hasReadError ? (
        <Stack spacing={1.5}>
          {orgState === "error" && (
            <ReadError
              message="Organization details could not be loaded. Try again."
              onRetry={onRetryOrg}
            />
          )}
          {termsState === "error" && (
            <ReadError
              message="Contract terms could not be loaded. Try again."
              onRetry={onRetryTerms}
            />
          )}
        </Stack>
      ) : isReadLoading || !readyOrg || terms === undefined ? (
        <Box
          role="status"
          aria-label="Loading approval details"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Stack spacing={2.5} divider={<Divider flexItem />}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { sm: "flex-start" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: "center", flexWrap: "wrap" }}
              >
                <Typography
                  component="h2"
                  variant="h6"
                  sx={{ fontWeight: 700 }}
                >
                  {orgBusinessName(readyOrg)}
                </Typography>
                <StatusBadge status={readyOrg.status} />
              </Stack>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{ mt: 0.25 }}
              >
                {orgLegalName(readyOrg) ?? "Legal name not provided"}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              disabled={actionPending}
              onClick={onClose}
            >
              Close
            </Button>
          </Stack>

          <LaunchReadiness
            org={readyOrg}
            owner={ownerDisplayName(readyOrg)}
            terms={terms}
            blockers={readyOrg.approval_blockers ?? []}
          />

          {(readyOrg.status === "pending" || readyOrg.status === "in_review") &&
            (readyOrg.approval_blockers?.length ?? 0) > 0 && (
              <ApprovalBlockersPanel
                blockers={readyOrg.approval_blockers ?? []}
              />
            )}

          <CommercialAgreement terms={terms} />

          {actionError && (
            <Alert severity="error" role="alert">
              {actionError}
            </Alert>
          )}

          {(readyOrg.status === "pending" ||
            readyOrg.status === "in_review") && (
            <FinalDecision
              org={readyOrg}
              actionPending={actionPending}
              startReviewPending={startReviewPending}
              approvePending={approvePending}
              onStartReview={onStartReview}
              onApprove={onApprove}
              onReject={onReject}
            />
          )}
        </Stack>
      )}
    </Box>
  );
}
